import React, { useCallback, useEffect, useState } from 'react';
import ExcelJS from 'exceljs';
import TallyService from '../../../services/TallyService';
import { useTranslation } from 'react-i18next';

const getSummaryRows = (response) => {
  const candidates = [response, response?.data];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
    for (const key of ['classFeeSummary', 'classWiseFees', 'classWiseFeeSummary', 'classes', 'students']) {
      if (Array.isArray(candidate?.[key])) return candidate[key];
    }
  }
  return [];
};

const ClassFeeSummary = ({ schoolId, academicYearId, onToast }) => {
  const { t } = useTranslation();
  const [allRows, setAllRows] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allRowsLoaded, setAllRowsLoaded] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedSection, setSelectedSection] = useState('all');

  const loadSummary = useCallback(async () => {
    try {
      setLoading(true);
      setAllRowsLoaded(false);
      setSelectedClass('all');
      setSelectedSection('all');
      const response = await TallyService.getClassFeeSummary(schoolId, academicYearId);
      const summaryRows = getSummaryRows(response);
      setAllRows(summaryRows);
      setRows(summaryRows);
      setAllRowsLoaded(true);
    } catch (error) {
      console.error('Error loading class fee summary:', error);
      onToast?.(t('errorLoadingClassFeeSummary'), 'error');
    } finally {
      setLoading(false);
    }
  }, [schoolId, academicYearId, onToast, t]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    if (!allRowsLoaded) return;
    if (selectedClass === 'all') {
      setRows(allRows);
      return;
    }

    const loadFilteredSummary = async () => {
      try {
        setLoading(true);
        const response = await TallyService.getClassFeeSummary(
          schoolId,
          academicYearId,
          selectedClass,
          selectedSection === 'all' ? undefined : selectedSection
        );
        setRows(getSummaryRows(response));
      } catch (error) {
        console.error('Error loading filtered class fee summary:', error);
        onToast?.(t('errorLoadingClassFeeSummary'), 'error');
      } finally {
        setLoading(false);
      }
    };

    loadFilteredSummary();
  }, [schoolId, academicYearId, selectedClass, selectedSection, allRowsLoaded, allRows, onToast, t]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'Pupilify';
      workbook.created = new Date();
      const worksheet = workbook.addWorksheet('Class Fee Summary');
      worksheet.columns = [
        { width: 26 }, { width: 22 }, { width: 18 }, { width: 18 },
        { width: 18 }, { width: 20 }, { width: 18 }
      ];

      const titleRow = worksheet.addRow([t('classFeeSummary')]);
      titleRow.font = { bold: true, size: 16, color: { argb: 'FF17365D' } };
      worksheet.mergeCells(1, 1, 1, 7);
      worksheet.addRow([t('class'), selectedClass === 'all'
        ? t('allClasses')
        : classOptions.find((item) => item.id === selectedClass)?.name || '',
      t('section'), selectedSection === 'all' ? t('allSections') : selectedSection]);
      worksheet.addRow([]);

      const headers = [
        t('studentName'), t('enrollmentId'), t('class'), t('section'),
        t('totalFee'), t('totalCollected'), t('totalDue')
      ];
      const headerRow = worksheet.addRow(headers);
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF17365D' } };
      worksheet.autoFilter = {
        from: { row: headerRow.number, column: 1 },
        to: { row: headerRow.number, column: headers.length }
      };

      filteredStudents.forEach((student) => {
        const row = worksheet.addRow([
          student.studentName || '-',
          student.enrollmentId || '-',
          student.className || '-',
          student.sectionName || '-',
          Number(student.totalFee) || 0,
          Number(student.collected) || 0,
          Number(student.due) || 0
        ]);
        [5, 6, 7].forEach((column) => {
          row.getCell(column).numFmt = '₹#,##0.00';
        });
      });

      worksheet.addRow([]);
      const totalsStartRow = worksheet.rowCount + 1;
      worksheet.addRow([t('totalStudents'), filteredStudents.length]);
      worksheet.addRow([t('totalFee'), totals.totalFee]);
      worksheet.addRow([t('totalCollected'), totals.collected]);
      worksheet.addRow([t('totalDue'), totals.due]);
      [totalsStartRow + 1, totalsStartRow + 2, totalsStartRow + 3].forEach((rowNumber) => {
        worksheet.getCell(rowNumber, 2).numFmt = '₹#,##0.00';
      });
      for (let rowNumber = totalsStartRow; rowNumber <= worksheet.rowCount; rowNumber += 1) {
        worksheet.getRow(rowNumber).font = { bold: true };
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'class-fee-summary.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      onToast?.(t('excelDownloadedSuccessfully'), 'success');
    } catch (error) {
      console.error('Error exporting class fee summary:', error);
      onToast?.(t('errorExportingExcel'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const flattenStudents = (sourceRows) => sourceRows.flatMap((summary) => {
    const summaryStudents = Array.isArray(summary.students) ? summary.students : [summary];
    return summaryStudents.map((student) => ({
    ...student,
    classId: student.classId ?? summary.classId,
    className: student.className || summary.className || '',
    sectionId: student.sectionId ?? summary.sectionId,
    sectionName: student.sectionName || summary.sectionName || ''
    }));
  });
  const allStudents = flattenStudents(allRows);
  const students = flattenStudents(rows);
  const classOptions = [...new Map(allStudents.map((student) => {
    const id = String(student.classId ?? student.className);
    return [id, { id, name: student.className || `${t('class')} ${student.classId}` }];
  })).values()];
  const classStudents = selectedClass === 'all'
    ? students
    : students.filter((student) => String(student.classId ?? student.className) === selectedClass);
  const sectionOptions = [...new Map(allStudents
    .filter((student) => selectedClass === 'all' || String(student.classId ?? student.className) === selectedClass)
    .filter((student) => student.sectionName)
    .map((student) => {
      const id = String(student.sectionId ?? student.sectionName);
      return [id, { id, name: student.sectionName }];
    })).values()];
  const filteredStudents = selectedSection === 'all'
    ? classStudents
    : classStudents.filter((student) => String(student.sectionId ?? student.sectionName) === selectedSection);
  const totals = filteredStudents.reduce((result, student) => ({
    totalFee: result.totalFee + (Number(student.totalFee) || 0),
    collected: result.collected + (Number(student.collected) || 0),
    due: result.due + (Number(student.due) || 0)
  }), { totalFee: 0, collected: 0, due: 0 });
  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', minimumFractionDigits: 2
  }).format(Number(amount) || 0);

  return (
    <section className="tally-class-fees">
      <div className="tally-class-fees-header">
        <h4>{t('classFeeSummary')}</h4>
        <button className="tally-export-btn" type="button" onClick={handleExport} disabled={exporting}>
          {exporting ? t('downloading') : t('exportToExcel')}
        </button>
      </div>
      {loading ? (
        <div className="tally-loading">{t('loadingTallyData')}</div>
      ) : rows.length === 0 ? (
        <div className="tally-empty-state"><p>{t('noClassFeeSummary')}</p></div>
      ) : (
        <>
          <div className="tally-fee-filters">
            <label>
              {t('class')}
              <select
                value={selectedClass}
                onChange={(event) => {
                  setSelectedClass(event.target.value);
                  setSelectedSection('all');
                }}
              >
                <option value="all">{t('allClasses')}</option>
                {classOptions.map((classOption) => (
                  <option key={classOption.id} value={classOption.id}>{classOption.name}</option>
                ))}
              </select>
            </label>
            <label>
              {t('section')}
              <select value={selectedSection} onChange={(event) => setSelectedSection(event.target.value)}>
                <option value="all">{t('allSections')}</option>
                {sectionOptions.map((section) => <option key={section.id} value={section.id}>{section.name}</option>)}
              </select>
            </label>
          </div>

          {filteredStudents.length === 0 ? (
            <div className="tally-empty-state"><p>{t('noStudentsFound')}</p></div>
          ) : (
            <div className="tally-table-container">
              <table className="tally-table tally-class-fees-table">
                <thead>
                  <tr>
                    <th>{t('studentName')}</th>
                    <th>{t('enrollmentId')}</th>
                    <th>{t('class')}</th>
                    <th>{t('section')}</th>
                    <th>{t('totalFee')}</th>
                    <th>{t('totalCollected')}</th>
                    <th>{t('totalDue')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map((student, index) => (
                    <tr key={student.studentId || student.enrollmentId || index}>
                      <td>{student.studentName || '-'}</td>
                      <td>{student.enrollmentId || '-'}</td>
                      <td>{student.className || '-'}</td>
                      <td>{student.sectionName || '-'}</td>
                      <td>{formatCurrency(student.totalFee)}</td>
                      <td className="tally-fee-collected">{formatCurrency(student.collected)}</td>
                      <td className="tally-fee-due">{formatCurrency(student.due)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="tally-fee-totals" aria-live="polite">
            <div><span>{t('totalStudents')}</span><strong>{filteredStudents.length}</strong></div>
            <div><span>{t('totalFee')}</span><strong>{formatCurrency(totals.totalFee)}</strong></div>
            <div><span>{t('totalCollected')}</span><strong className="tally-fee-collected">{formatCurrency(totals.collected)}</strong></div>
            <div><span>{t('totalDue')}</span><strong className="tally-fee-due">{formatCurrency(totals.due)}</strong></div>
          </div>
        </>
      )}
    </section>
  );
};

export default ClassFeeSummary;